import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App';
import { Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import AuthExperienceShell from './AuthExperienceShell';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [keepLoggedIn, setKeepLoggedIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  // loginPending: true while we're waiting for the AuthContext user state to commit
  const [loginPending, setLoginPending] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check for messages from registration or other flows
    const { message, email, showProfileSetupMessage } = location.state || {};
    if (message && showProfileSetupMessage) {
      setSuccess(message);
    } else if (message) {
      // OAuth / redirect failures (RoadOps, Apple, Google) route an explanatory
      // message here. Previously it was silently dropped unless
      // showProfileSetupMessage was also set — so failed sign-ins landed on a
      // blank /login with no reason shown. Surface it as an error banner.
      setError(message);
    }
    if (email) setFormData(prev => ({ ...prev, email }));
  }, [location.state]);

  // Navigate AFTER React has committed the user state — eliminates the race condition
  // that caused the "double login" bug (setTimeout was unreliable).
  useEffect(() => {
    if (!loginPending || !user) return;
    setLoginPending(false);
    navigate('/dashboard', { replace: true });
  }, [user, loginPending]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(formData.email, formData.password, keepLoggedIn);
      // Signal the useEffect to navigate once user is committed to context
      setLoginPending(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <AuthExperienceShell
      badge="Secure Authentication"
      title="Welcome Back"
      description="Sign in to access your role-specific command center, membership tools, and operational workflows."
      backTo="/"
      backLabel="Back to Home"
      testId="login-experience-shell"
    >
      <div>
        <div className="mb-6 text-center">
          <Link to="/" className="inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 mb-3" data-testid="login-brand-home-link">
            <img
              src="/favicon-192x192.png?v=20260609"
              alt="HopSeat Logo"
              className="h-8 w-8 object-contain"
            />
            <span className="text-xl font-bold hopseat-brand">HopSeat</span>
          </Link>
        </div>

        <div className="rounded-xl border border-white/10 bg-black/20 p-4 md:p-5">
          {error && (
            <div className="alert alert-error mb-4" data-testid="login-error-alert">
              <AlertCircle className="h-5 w-5 inline mr-2" />
              {error}
            </div>
          )}

          {success && (
            <div className="alert alert-success mb-4" data-testid="login-success-alert">
              <CheckCircle className="h-5 w-5 inline mr-2" />
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} data-testid="login-form">
            {/* Google Sign-In Button */}
            <button
              type="button"
              onClick={() => {
                const apiBase = window.location.origin;
                window.location.href = `${apiBase}/api/auth/google/login?role=driver`;
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 mb-3 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-white font-medium transition-all"
              data-testid="google-signin-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>

            {/* Apple Sign-In Button — Apple HIG compliant (black bg, white logo + text) */}
            <button
              type="button"
              onClick={() => {
                const apiBase = window.location.origin;
                window.location.href = `${apiBase}/api/auth/apple/login?role=driver`;
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 mb-4 rounded-lg bg-black text-white font-medium hover:bg-neutral-900 transition-all border border-black"
              data-testid="apple-signin-button"
              aria-label="Sign in with Apple"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#FFFFFF" aria-hidden="true">
                <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Sign in with Apple
            </button>

            {/* RoadOps Sign-In Button — partner OIDC integration */}
            <button
              type="button"
              onClick={() => {
                const apiBase = window.location.origin;
                window.location.href = `${apiBase}/api/auth/roadops/login?role=driver`;
              }}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 mb-4 rounded-lg border border-white/20 bg-white/5 hover:bg-white/10 text-white font-medium transition-all"
              data-testid="roadops-signin-button"
              aria-label="Sign in with RoadOps"
            >
              <img src="/roadops-logo.png?v=20260609" alt="" className="w-5 h-5 object-contain" aria-hidden="true" />
              Sign in with RoadOps
            </button>

            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gray-900/80 px-3 text-gray-500">or sign in with email</span>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                placeholder="Enter your email"
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="form-input pr-12"
                  required
                  placeholder="Enter your password"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-200"
                  data-testid="password-visibility-toggle"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label className="flex items-center justify-between">
                <div className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={keepLoggedIn}
                    onChange={(e) => setKeepLoggedIn(e.target.checked)}
                    className="rounded border-gray-600 text-orange-500 focus:ring-orange-500 focus:ring-offset-gray-800"
                  />
                  <span className="text-sm text-gray-300">Keep me logged in</span>
                </div>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-blue-400 hover:text-blue-300 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary flex items-center justify-center"
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <div className="loading-spinner mr-2"></div>
                  Signing In...
                </>
              ) : (
                'Sign In to HopSeat'
              )}
            </button>
          </form>

          {/* Demo accounts moved to admin portal */}

          <div className="mt-6 text-center">
            <p className="text-gray-300">
              Don&apos;t have a HopSeat account?{' '}
              <Link to="/register" className="text-orange-500 hover:text-orange-400 font-semibold">
                Sign up here
              </Link>
            </p>
          </div>
        </div>
      </div>
    </AuthExperienceShell>
  );
};

export default Login;